import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  City,
  Country,
  State,
} from "country-state-city";

import {
  phoneCountryCodeOptions,
} from "../../../shared/config/phoneOptions";

const inputClass =
  "mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:bg-slate-100";

const blankAddress = {
  houseNo: "",
  street: "",
  area: "",
  country: "",
  state: "",
  city: "",
  pin: "",
};

const serializeAddress = (
  value
) =>
  [
    value.houseNo,
    value.street,
    value.area,
    value.city,
    value.state,
    value.country,
    value.pin,
  ]
    .filter(Boolean)
    .join(", ");

const AddressEditor = ({
  title,
  value,
  onChange,
  disabled = false,
}) => {
  const countries =
    useMemo(
      () =>
        Country.getAllCountries(),
      []
    );

  const states =
    value.country
      ? State.getStatesOfCountry(
          value.country
        )
      : [];

  const cities =
    value.country &&
    value.state
      ? City.getCitiesOfState(
          value.country,
          value.state
        )
      : [];

  const change = (
    field,
    nextValue
  ) => {
    const next = {
      ...value,
      [field]:
        nextValue,
    };

    if (field === "country") {
      next.state = "";
      next.city = "";
    }

    if (field === "state") {
      next.city = "";
    }

    onChange(next);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
      <h4 className="mb-4 text-sm font-semibold text-slate-800">
        {title}
      </h4>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="text-sm font-medium text-slate-700">
          House No / Flat No
          <input
            value={value.houseNo}
            disabled={disabled}
            onChange={(event) =>
              change(
                "houseNo",
                event.target.value
              )
            }
            className={inputClass}
          />
        </label>

        <label className="text-sm font-medium text-slate-700">
          Street
          <input
            value={value.street}
            disabled={disabled}
            onChange={(event) =>
              change(
                "street",
                event.target.value
              )
            }
            className={inputClass}
          />
        </label>

        <label className="text-sm font-medium text-slate-700">
          Area / Locality
          <input
            value={value.area}
            disabled={disabled}
            onChange={(event) =>
              change(
                "area",
                event.target.value
              )
            }
            className={inputClass}
          />
        </label>

        <label className="text-sm font-medium text-slate-700">
          Country
          <select
            value={value.country}
            disabled={disabled}
            onChange={(event) =>
              change(
                "country",
                event.target.value
              )
            }
            className={inputClass}
          >
            <option value="">
              Select Country
            </option>
            {countries.map(
              (country) => (
                <option
                  key={
                    country.isoCode
                  }
                  value={
                    country.isoCode
                  }
                >
                  {country.name}
                </option>
              )
            )}
          </select>
        </label>

        <label className="text-sm font-medium text-slate-700">
          State
          <select
            value={value.state}
            disabled={
              disabled ||
              !states.length
            }
            onChange={(event) =>
              change(
                "state",
                event.target.value
              )
            }
            className={inputClass}
          >
            <option value="">
              Select State
            </option>
            {states.map(
              (state) => (
                <option
                  key={state.isoCode}
                  value={state.isoCode}
                >
                  {state.name}
                </option>
              )
            )}
          </select>
        </label>

        <label className="text-sm font-medium text-slate-700">
          City
          <select
            value={value.city}
            disabled={
              disabled ||
              !cities.length
            }
            onChange={(event) =>
              change(
                "city",
                event.target.value
              )
            }
            className={inputClass}
          >
            <option value="">
              Select City
            </option>
            {cities.map((city) => (
              <option
                key={city.name}
                value={city.name}
              >
                {city.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm font-medium text-slate-700">
          PIN / Postal Code
          <input
            value={value.pin}
            disabled={disabled}
            onChange={(event) =>
              change(
                "pin",
                event.target.value
              )
            }
            className={inputClass}
          />
        </label>
      </div>
    </div>
  );
};

const ContactAddressReferencesSection = ({
  formData,
  setFieldValue,
  updateField,
}) => {
  const [alternateCode, setAlternateCode] =
    useState("+91");
  const [alternateNumber, setAlternateNumber] =
    useState("");

  const [guardianCode, setGuardianCode] =
    useState("+91");
  const [guardianNumber, setGuardianNumber] =
    useState("");

  const [reference1Code, setReference1Code] =
    useState("+91");
  const [reference1Number, setReference1Number] =
    useState("");

  const [reference2Code, setReference2Code] =
    useState("+91");
  const [reference2Number, setReference2Number] =
    useState("");

  const [communication, setCommunication] =
    useState(blankAddress);

  const [residence, setResidence] =
    useState(blankAddress);

  const [sameAddress, setSameAddress] =
    useState(false);

  useEffect(() => {
    setFieldValue(
      "alternatePhone",
      alternateNumber
        ? `${alternateCode}${alternateNumber}`
        : ""
    );
  }, [
    alternateCode,
    alternateNumber,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue(
      "guardianPhone",
      guardianNumber
        ? `${guardianCode}${guardianNumber}`
        : ""
    );
  }, [
    guardianCode,
    guardianNumber,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue(
      "reference1Phone",
      reference1Number
        ? `${reference1Code}${reference1Number}`
        : ""
    );
  }, [
    reference1Code,
    reference1Number,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue(
      "reference2Phone",
      reference2Number
        ? `${reference2Code}${reference2Number}`
        : ""
    );
  }, [
    reference2Code,
    reference2Number,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue(
      "communicationAddress",
      serializeAddress(
        communication
      )
    );
    setFieldValue(
      "communicationState",
      communication.state
    );

    if (sameAddress) {
      setResidence({
        ...communication,
      });
    }
  }, [
    communication,
    sameAddress,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue(
      "residenceAddress",
      serializeAddress(
        residence
      )
    );
    setFieldValue(
      "residenceState",
      residence.state
    );
  }, [
    residence,
    setFieldValue,
  ]);

  const phoneControl = ({
    label,
    code,
    setCode,
    number,
    setNumber,
    required = false,
  }) => (
    <div>
      <div className="mb-1 text-sm font-medium text-slate-700">
        {label}
        {required && (
          <span className="ml-1 text-red-600">
            *
          </span>
        )}
      </div>
      <div className="grid grid-cols-[165px_1fr] gap-2">
        <select
          value={code}
          onChange={(event) =>
            setCode(
              event.target.value
            )
          }
          className={inputClass}
        >
          {phoneCountryCodeOptions.map(
            (option) => (
              <option
                key={`${option.iso2}-${option.code}`}
                value={option.code}
              >
                {option.label}
              </option>
            )
          )}
        </select>

        <input
          type="tel"
          value={number}
          onChange={(event) =>
            setNumber(
              event.target.value.replace(
                /\D/g,
                ""
              )
            )
          }
          maxLength={10}
          inputMode="numeric"
          className={inputClass}
          placeholder="10-digit mobile number"
        />
      </div>
      {number &&
        number.length !== 10 && (
          <p className="mt-1 text-xs text-red-600">
            Enter a valid 10-digit phone number.
          </p>
        )}
    </div>
  );

  return (
    <div className="space-y-7">
      <div>
        <h3 className="mb-4 text-base font-semibold text-slate-800">
          Other Contact Details
        </h3>
        <div className="grid gap-5 md:grid-cols-2">
          {phoneControl({
            label:
              "Alternate Phone",
            code: alternateCode,
            setCode:
              setAlternateCode,
            number:
              alternateNumber,
            setNumber:
              setAlternateNumber,
          })}

          {phoneControl({
            label:
              "Guardian Phone",
            code: guardianCode,
            setCode:
              setGuardianCode,
            number:
              guardianNumber,
            setNumber:
              setGuardianNumber,
          })}
        </div>
      </div>

      <AddressEditor
        title="Communication Address"
        value={communication}
        onChange={setCommunication}
      />

      <div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={sameAddress}
            onChange={(event) => {
              const checked =
                event.target.checked;

              setSameAddress(
                checked
              );

              if (checked) {
                setResidence({
                  ...communication,
                });
              }
            }}
          />
          Same as Communication Address
        </label>
        <p className="mt-1 text-xs text-slate-500">
          When selected, Residence Address stays synchronized with Communication Address.
        </p>
      </div>

      <AddressEditor
        title="Residence Address"
        value={residence}
        onChange={setResidence}
        disabled={sameAddress}
      />

      <div className="border-t border-slate-200 pt-6">
        <h3 className="mb-2 text-base font-semibold text-slate-800">
          References
        </h3>
        <p className="mb-4 text-sm text-slate-500">
          Please provide a person who knows you and your family well. Reference 1 is required.
        </p>

        <div className="grid gap-5 md:grid-cols-2">
          <label className="text-sm font-medium text-slate-700">
            Reference 1 Name
            <span className="ml-1 text-red-600">*</span>
            <input
              name="reference1Name"
              value={
                formData.reference1Name ||
                ""
              }
              onChange={updateField}
              className={inputClass}
            />
          </label>

          {phoneControl({
            label:
              "Reference 1 Phone",
            code: reference1Code,
            setCode:
              setReference1Code,
            number:
              reference1Number,
            setNumber:
              setReference1Number,
            required: true,
          })}

          <label className="text-sm font-medium text-slate-700">
            Reference 2 Name
            <input
              name="reference2Name"
              value={
                formData.reference2Name ||
                ""
              }
              onChange={updateField}
              className={inputClass}
            />
          </label>

          {phoneControl({
            label:
              "Reference 2 Phone",
            code: reference2Code,
            setCode:
              setReference2Code,
            number:
              reference2Number,
            setNumber:
              setReference2Number,
          })}

        </div>
      </div>
    </div>
  );
};

export default ContactAddressReferencesSection;
